import React, { useState } from "react";
import InputBar from "components/InputBar/InputBar";
import Button from "components/Button/Button";
import Timer from "components/Timer/Timer";
import OtpSelector from "components/OtpSelector/OtpSelector";
import { withRouter } from "react-router";
import { APP_ROUTES } from "constants/routes";
import { API_KEYS } from "constants/apiEndpoints";
import { requestPostFetchApi } from "utils/apiMethods";
import { isMobileNumberIndian } from "utils/validations";
import { OTP_LENGTH, RESEND_OTP_TIME } from "constants/constants";
import { getMobileNumberWithCountryCode } from "utils/commonMethods";
import useAuthentication from "hooks/useAuthentication";
import withToasts from "hoc/withToasts";
import "components/LoginForm/LoginForm.css";
import { LeftArrowIcon } from "constants/icons";

//Handles user login with OTP verifications
function LoginForm({ showErrorToast, showSuccessToast, history }) {
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [resendOtpEnabled, setResendOtpEnabled] = useState(false);
  const { setUserToken } = useAuthentication();

  const componentTexts = window.appTexts.components.login_form;

  const sendOtpToUser = function () {
    return requestPostFetchApi(API_KEYS.SEND_OTP, {
      phone_no: getMobileNumberWithCountryCode(mobileNumber),
    });
  };

  const toggleOtpScreen = function () {
    return setShowOtpScreen(!showOtpScreen);
  };

  const mobileNumberChangeHandler = function (name, value) {
    return setMobileNumber(value);
  };

  const handleMobileNumberOtpMiddleware = function (errorToastEnabled = false) {
    //Validates mobile number beforehand.
    if (!isMobileNumberIndian(mobileNumber)) {
      if (errorToastEnabled) {
        showErrorToast(
          componentTexts.invalid_mobile_number_title,
          componentTexts.invalid_mobile_number_description
        );
      }
      return undefined;
    }
    return true;
  };

  const handleMobileNumberOtp = function () {
    if (!handleMobileNumberOtpMiddleware(true)) {
      return;
    }
    return toggleOtpScreen();
  };

  const onResendTimerComplete = function () {
    return setResendOtpEnabled(true);
  };

  const resendOtp = function () {
    return sendOtpToUser().then(() => {
      showSuccessToast(
        componentTexts.otp_resent_title,
        componentTexts.otp_resent_description_prefix +
          mobileNumber +
          componentTexts.otp_resent_description_postfix
      );
      return setResendOtpEnabled(false);
    });
  };

  const mobileNumberEditHandler = function () {
    toggleOtpScreen();
  };

  const onOtpChange = function (nextOtp) {
    return setOtp(nextOtp);
  };

  const redirectUserToCheckoutPage = function () {
    return history.push(APP_ROUTES.CHECKOUT_PAGE.url);
  };

  const submitHandlerMiddleware = function (errorToastEnabled = false) {
    if (!otp.length === OTP_LENGTH) {
      if (errorToastEnabled) {
        showErrorToast(
          componentTexts.invalid_otp_title,
          componentTexts.invalid_otp_description
        );
      }
      return undefined;
    }
    return true;
  };
  const submitHandler = function (response) {
    if (!submitHandlerMiddleware(true)) {
      return;
    }
    //Handles otp not verified
    if (!response.otpVerified) {
      //If no attempts are left, send a new otp.
      if (response.otp_verification_attempts === 0) {
        return sendOtpToUser().then(() => {
          return showSuccessToast(
            componentTexts.new_otp_resend_title,
            componentTexts.new_otp_resend_description
          );
        });
      }
      return showErrorToast(
        componentTexts.invalid_otp_title,
        componentTexts.invalid_otp_description +
          response.otp_verification_attempts
      );
    } else {
      setUserToken(response.user);
      //Redirect user to checkout page.
      redirectUserToCheckoutPage();
    }
  };

  const verifyOtpRequestBody = {
      otp,
      phone_no: getMobileNumberWithCountryCode(mobileNumber),
    },
    sendOtpRequestBody = {
      phone_no: getMobileNumberWithCountryCode(mobileNumber),
    };

  return (
    <div className="login-form">
      <div className="login-form__head">
        <h3 className="login-form__title">
          {showOtpScreen
            ? componentTexts.page_title_after_otp
            : componentTexts.page_title_before_otp}
        </h3>

        {showOtpScreen ? (
          <div className="login-form__description-otp-screen">
            <span> {getMobileNumberWithCountryCode(mobileNumber)}</span>
            <span
              onClick={mobileNumberEditHandler}
              className="fcolor--secondary cursor--pointer"
            >
              {componentTexts.edit_mobile_number}
            </span>
          </div>
        ) : (
          <span className="login-form__description">
            {componentTexts.description_before_otp}
          </span>
        )}
      </div>
      <div className="login-form__input-bar-wrapper">
        {showOtpScreen ? (
          <OtpSelector onChange={onOtpChange} />
        ) : (
          <InputBar
            type="number"
            value={mobileNumber}
            onChange={mobileNumberChangeHandler}
            inputProps={{
              placeholder: componentTexts.input_placeholder_before_otp,
            }}
          />
        )}
      </div>
      {showOtpScreen && (
        <div className="login-form__resend-otp">
          {!resendOtpEnabled ? (
            <Timer
              concatenateText={componentTexts.resend_otp_waiting}
              seconds={RESEND_OTP_TIME}
              onTimerComplete={onResendTimerComplete}
            />
          ) : (
            <React.Fragment>
              <span>{componentTexts.resend_otp_text_prefix}</span>
              <span
                className="fcolor--secondary cursor--pointer"
                onClick={resendOtp}
              >
                {componentTexts.resend_otp_text}
              </span>
            </React.Fragment>
          )}
        </div>
      )}
      <div className="login-form__actions">
        {showOtpScreen ? (
          <Button
            onClick={submitHandler}
            loaderApiKey={submitHandlerMiddleware() && API_KEYS.VERIFY_OTP}
            apiRequestBody={verifyOtpRequestBody}
          >
            {componentTexts.button_text_after_otp}
          </Button>
        ) : (
          <Button
            loaderApiKey={
              handleMobileNumberOtpMiddleware() && API_KEYS.SEND_OTP
            }
            apiRequestBody={sendOtpRequestBody}
            onClick={handleMobileNumberOtp}
          >
            {componentTexts.button_text_before_otp}
          </Button>
        )}
      </div>
      <hr className="login-form__divider" />
      <div className="login-form__guest fcolor--secondary">
        <span onClick={redirectUserToCheckoutPage}>
          {componentTexts.continue_as_guest}
          <i>
            <LeftArrowIcon />
          </i>
        </span>
      </div>
    </div>
  );
}

LoginForm.propTypes = {};

export default withToasts(withRouter(LoginForm));
